import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store';
import { selectParticipantId } from 'src/app/features/session/store/selectors/session.selectors';
import { ERROR_MESSAGES } from 'src/app/features/session/constants/error-messages';
import { FaceDetectionService } from 'src/app/features/face-detection/services/face-detection.service';
import { FaceDetectionResponse } from 'src/app/features/face-detection/responses/face-detection.response';

interface FaceDetectionState {
  isFaceDetected: boolean;
  isKnownFace: boolean;
  knownFaceConfidence: number;
  userSnapshotBase64Image: string | null;
  isFaceComparisonLoading: boolean;
  faceComparisonErrorMessage: string | null;
  isSnapshotLoading: boolean;
  snapshotErrorMessage: string | null;
}

const initialState: FaceDetectionState = {
  isFaceDetected: false,
  isKnownFace: false,
  knownFaceConfidence: 0,
  userSnapshotBase64Image: null,
  isFaceComparisonLoading: false,
  faceComparisonErrorMessage: null,
  isSnapshotLoading: false,
  snapshotErrorMessage: null,
};

export const FaceDetectionStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      faceDetectionService = inject(FaceDetectionService),
      appStore = inject(Store<AppState>),
    ) => ({
      async compareFace(base64Image: string): Promise<void> {
        patchState(store, {
          isFaceComparisonLoading: true,
          faceComparisonErrorMessage: null,
        });

        const participantId = appStore.selectSignal(selectParticipantId)();

        if (!participantId) {
          patchState(store, {
            isFaceComparisonLoading: false,
            faceComparisonErrorMessage: ERROR_MESSAGES.PARTICIPANT_ID_NOT_AVAILABLE,
          });
          throw new Error(ERROR_MESSAGES.PARTICIPANT_ID_NOT_AVAILABLE);
        }

        try {
          const result: FaceDetectionResponse = await firstValueFrom(
            faceDetectionService.compareFace(participantId, base64Image),
          );

          patchState(store, {
            isFaceDetected: result.isFaceDetected,
            isKnownFace: result.isKnownFace,
            knownFaceConfidence: result.knownFaceConfidence,
            isFaceComparisonLoading: false,
            faceComparisonErrorMessage: null,
          });
        } catch (error) {
          patchState(store, {
            isFaceComparisonLoading: false,
            faceComparisonErrorMessage: 'Face comparison request failed',
          });
          throw error;
        }
      },

      async getUserSnapshot(): Promise<void> {
        patchState(store, {
          isSnapshotLoading: true,
          snapshotErrorMessage: null,
        });

        const participantId = appStore.selectSignal(selectParticipantId)();

        if (!participantId) {
          patchState(store, {
            isSnapshotLoading: false,
            snapshotErrorMessage: ERROR_MESSAGES.PARTICIPANT_ID_NOT_AVAILABLE,
          });
          throw new Error(ERROR_MESSAGES.PARTICIPANT_ID_NOT_AVAILABLE);
        }

        try {
          const response = await firstValueFrom(
            faceDetectionService.getUserSnapshot(participantId),
          );

          patchState(store, {
            userSnapshotBase64Image: response.snapshotBase64Image,
            isSnapshotLoading: false,
            snapshotErrorMessage: null,
          });
        } catch (error) {
          patchState(store, {
            isSnapshotLoading: false,
            snapshotErrorMessage: 'Failed to get user snapshot',
          });
          throw error;
        }
      },
    }),
  ),
);
